import { useState } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { ContentSection, DividerList, LabeledListItem } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { Card, Container, Image, Stack } from '@optimus-web/core';
import { useGetAvatarSrc } from '@mews-commander/utils';
import { ActorProfile } from '@mews-commander/data-access';
import { ActorProfilesSection } from '../../../../components';
import { useCurrentAccountingEnvironment } from '../../hooks/useCurrentAccountingEnvironment';
import { LanguageSwitcher } from './LanguageSwitcher';

interface Props {
    createdUtc: string;
    creatorProfile?: ActorProfile;
    descriptions: Record<string, string>;
    externalIdentifier?: string | null;
    externalNames?: Record<string, string>;
    imageId?: string | null;
    names: Record<string, string>;
    ordering: number;
    shortNames: Record<string, string>;
    units?: Record<string, string>;
    updatedUtc: string;
    updaterProfile?: ActorProfile;
}
export const GeneralTabContent = ({
    createdUtc,
    creatorProfile,
    descriptions,
    externalIdentifier,
    externalNames,
    imageId,
    names,
    ordering,
    shortNames,
    units,
    updatedUtc,
    updaterProfile,
}: Props) => {
    const { defaultLanguageCode } = useCurrentEnterprise();
    const accountingEnvironment = useCurrentAccountingEnvironment();

    const productLanguages = Object.keys({ ...names, ...externalNames });

    const [languageCode, setLanguageCode] = useState(
        productLanguages.includes(defaultLanguageCode) ? defaultLanguageCode : productLanguages[0]
    );

    const getImgSrc = useGetAvatarSrc();
    const productImageUrl = getImgSrc(imageId);

    return (
        <Stack spacing="400" vertical>
            <ContentSection
                title={lt('DetailPlural')}
                rightContent={
                    <Container width="200px">
                        <LanguageSwitcher
                            onLanguageChange={setLanguageCode}
                            productLanguages={productLanguages}
                            selectedLanguageCode={languageCode}
                        />
                    </Container>
                }
            >
                <DividerList>
                    <LabeledListItem label={lt('Name')}>{names[languageCode]}</LabeledListItem>
                    <LabeledListItem label={lt('ShortName')}>
                        {shortNames[languageCode] ?? UnicodeEntity.Dash}
                    </LabeledListItem>
                    <LabeledListItem label={lt('Description')}>
                        {descriptions[languageCode] ?? UnicodeEntity.Dash}
                    </LabeledListItem>
                    {externalNames ? (
                        <LabeledListItem label={lt('ExternalName')}>
                            {externalNames[languageCode] ?? UnicodeEntity.Dash}
                        </LabeledListItem>
                    ) : null}
                    <LabeledListItem label={lt('Ordering')}>{ordering}</LabeledListItem>
                    {accountingEnvironment.useBillingUnits && units ? (
                        <LabeledListItem label={lt('UnitPlural')}>
                            {units[languageCode] ?? UnicodeEntity.Dash}
                        </LabeledListItem>
                    ) : null}
                </DividerList>
            </ContentSection>
            {productImageUrl && (
                <ContentSection title={lt('Image')}>
                    <Card padding="200" outline>
                        <Stack alignment="center">
                            <Image src={productImageUrl} />
                        </Stack>
                    </Card>
                </ContentSection>
            )}
            <ContentSection title={lt('Mapping')}>
                <LabeledListItem label={lt('ExternalIdentifier')}>
                    {externalIdentifier ?? UnicodeEntity.Dash}
                </LabeledListItem>
            </ContentSection>

            {creatorProfile && updaterProfile ? (
                <ActorProfilesSection
                    createdUtc={createdUtc}
                    creatorProfile={creatorProfile}
                    updatedUtc={updatedUtc}
                    updaterProfile={updaterProfile}
                />
            ) : null}
        </Stack>
    );
};
